import React, { useMemo } from 'react';
import { Select } from 'antd';
import type { SelectProps } from 'antd';
import { useOpportunities } from '@/hooks/useOpportunities';
import type { Opportunity } from '@/services/opportunity.service';

export type EpcOpportunitySelectProps = Omit<
  SelectProps<string>,
  'options' | 'loading' | 'showSearch' | 'optionFilterProp'
> & {
  /** When set, only opportunities for this Customer (client) are shown (client-side filter). */
  customerId?: string | null;
  onOpportunityChange?: (opportunity: Opportunity | undefined) => void;
};

/**
 * Searchable Opportunity select — never raw UUID entry.
 * Optional customerId filters the list to that Client.
 */
export const EpcOpportunitySelect: React.FC<EpcOpportunitySelectProps> = ({
  customerId,
  onOpportunityChange,
  onChange,
  allowClear = true,
  placeholder = 'Select opportunity',
  ...rest
}) => {
  const { data: opportunities = [], isLoading } = useOpportunities();

  const filtered = useMemo(() => {
    const list = Array.isArray(opportunities) ? opportunities : [];
    if (!customerId) return list;
    return list.filter((o) => o.customer_id === customerId);
  }, [opportunities, customerId]);

  const options = filtered.map((o) => ({
    label: o.opportunity_number
      ? `${o.opportunity_number} — ${o.opportunity_name}`
      : o.opportunity_name,
    value: o.id,
  }));

  return (
    <Select
      {...rest}
      allowClear={allowClear}
      placeholder={placeholder}
      loading={isLoading}
      showSearch
      optionFilterProp="label"
      options={options}
      onChange={(value, option) => {
        onChange?.(value, option);
        if (onOpportunityChange) {
          if (!value) {
            onOpportunityChange(undefined);
            return;
          }
          const list = Array.isArray(opportunities) ? opportunities : [];
          onOpportunityChange(list.find((o) => o.id === value));
        }
      }}
    />
  );
};

export default EpcOpportunitySelect;
